import { ArrowLeftIcon } from "@phosphor-icons/react";
import { Button } from "@bednbody/ui/components/button";
import { Separator } from "@bednbody/ui/components/separator";
import { SidebarInset, SidebarProvider, SidebarTrigger } from "@bednbody/ui/components/sidebar";
import { Toaster } from "@bednbody/ui/components/sonner";
import { cn } from "@bednbody/ui/lib/utils";
import { Outlet, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { drilledInFrom } from "@/lib/drillIn";
import { NotificationBell } from "@/features/notifications/NotificationBell";
import { useNotificationsRealtime } from "@/features/notifications/notifications.hooks";
import {
  ATTENTION_TOASTER_ID,
  useNotificationToasts,
} from "@/features/notifications/useNotificationToasts";
import { BookingDetailModal } from "@/features/ops-dashboard/components/BookingDetailModal";
import { SosAlerts } from "@/features/sos/SosAlerts";
import { useAuth } from "@/lib/auth";
import { navSectionFor } from "@/lib/permissions";
import { AppSidebar } from "./AppSidebar";
import { PAGE_TOOLBAR_ID } from "./PageToolbar";

const PAGE_TITLES: Record<string, string> = {
  "/": "Ops Dashboard",
  "/bookings": "Bookings",
  "/attendance": "Attendance",
  "/dispatch": "Dispatch",
  "/calendar": "Operational Calendar",
  "/live-map": "Live Map",
  "/roster": "Roster",
  "/documents": "Documents",
  "/cash": "Cash Remittance",
  "/mileage": "Mileage Rate",
  "/payroll": "Payroll",
  "/giftpass": "GiftPass",
  "/analytics": "Analytics",
  "/catalog": "Service Catalog",
  "/hubs": "Hubs",
  "/audit": "Audit Log",
};

function pageTitle(path: string): string {
  // A drill-in origin may carry its query (`/live-map?demo=1`); the title is the page's.
  const pathname = path.split("?")[0] ?? path;
  return pathname.startsWith("/hubs/") ? "Hub details" : (PAGE_TITLES[pathname] ?? "Dashboard");
}

export function AppLayout() {
  const { claims, session, signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // 0278 — notifications reach the bell live, and new ones pop up top-right.
  useNotificationsRealtime();
  useNotificationToasts();
  if (!claims) return null;

  // `?booking=<id>` opens that booking on ANY page — the one link shape a
  // notification, a toast or the live map uses to land on a booking. Closing
  // drops the param and keeps the drill-in, so "← Back to …" survives.
  const linkedBookingId = searchParams.get("booking");
  function closeLinkedBooking() {
    const next = new URLSearchParams(searchParams);
    next.delete("booking");
    setSearchParams(next, { replace: true, state: location.state });
  }

  const section = navSectionFor(location.pathname);
  const email = session?.user.email ?? null;
  // Set only by a drill-in (see `drillInState`), never by the sidebar — a
  // sidebar click is a new place to be, not a detour to come back from.
  const cameFrom = drilledInFrom(location.state);
  // The live map runs edge to edge, and its height is sized against this
  // padding (`100vh - 7rem`), so it keeps the old frame.
  const fullBleed = location.pathname === "/live-map";
  // The calendar takes the same edge-to-edge frame (B0), but keeps the SOS
  // banner above it: unlike the map, it has no SOS lane of its own.
  const fullScreen = fullBleed || location.pathname === "/calendar";
  // B0 (Leon, 2026-10-23): data pages use the whole screen — the 1280 px cap
  // left wide empty margins on a PC. Pages that are mostly one form stay a
  // readable centred column.
  const formPage =
    location.pathname === "/settings" ||
    location.pathname === "/app-status" ||
    location.pathname === "/legal" ||
    location.pathname.startsWith("/hubs/");

  return (
    <SidebarProvider defaultOpen={true}>
      <AppSidebar role={claims.role} pathname={location.pathname} email={email} signOut={signOut} />

      <SidebarInset className="min-w-0">
        <header className="flex h-16 shrink-0 items-center gap-3 overflow-hidden px-6">
          <SidebarTrigger className="shrink-0" />
          <Separator className="h-5" orientation="vertical" />

          {cameFrom ? (
            <Button className="shrink-0" onClick={() => navigate(-1)} size="sm" variant="outline">
              <ArrowLeftIcon data-icon="inline-start" />
              Back to {pageTitle(cameFrom)}
            </Button>
          ) : null}

          {/* The page's own name is the h1, inside the page (PageHeader). Naming
              the group here instead keeps one h1 per document, and still says
              where you are when the sidebar is collapsed to icons — which is
              exactly when its group labels fade out (sidebar.tsx). */}
          {section ? (
            <span className="min-w-0 truncate text-sm font-medium text-muted-foreground">
              {section}
            </span>
          ) : null}

          <div
            className="flex min-w-0 flex-1 items-center gap-3 empty:hidden"
            id={PAGE_TOOLBAR_ID}
          />

          <NotificationBell />
        </header>

        <main className={cn("flex min-w-0 flex-1 flex-col", fullScreen ? "p-6" : "px-6 py-6")}>
          <div
            className={cn(
              "flex w-full min-w-0 flex-1 flex-col gap-6",
              formPage ? "mx-auto max-w-5xl" : null,
            )}
          >
            {/* 0273 — above every page, until someone acknowledges it. The live
                map pins it on the map and in its own lane instead (BL-18/BL-19). */}
            {fullBleed ? null : <SosAlerts />}
            <Outlet />
          </div>
        </main>
      </SidebarInset>

      <BookingDetailModal
        bookingId={linkedBookingId}
        isOpen={linkedBookingId !== null}
        onClose={closeLinkedBooking}
      />
      {/* New notifications, top-right under the header. The live map keeps its
          own stack (aligned under the SOS lane), and these join it there. */}
      {location.pathname === "/live-map" ? null : (
        <Toaster
          expand
          gap={8}
          id={ATTENTION_TOASTER_ID}
          offset={{ top: 72, right: 24 }}
          position="top-right"
          richColors
          visibleToasts={3}
        />
      )}
    </SidebarProvider>
  );
}
